import React, { useState } from 'react';

import { contactApis, dashboardContactApis } from '@apis/next/contactApis';
import infoIcon from '@assets/img/icons/info.svg';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { QueryContainer } from '@components/QueryContainer';
import { useBookingCreateContext } from '@components/utilities/BookingInquiry/BookingCreateContext';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import { ContactFilter } from '@components/utilities/BookingInquiry/ContactFilter';
import { ContactForm } from '@components/utilities/Contact/ContactForm';
import { VerticalSpace } from '@components/VerticalSpace';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { contactStrings } from '@data/contacts';
import { Cargo2 } from '@models/BookingInquiry';
import {
  type ContactForm as ContactFormType,
  contactForm,
  contactFormInitialValues,
} from '@models/Contact';
import { ContactSearchQuery } from '@models/next/Contact';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, formStrings, useErrorMessage } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { Form, Formik, useField } from 'formik';

const strings = createStrings({
  ja: {
    clientChooseShipper: 'SHIPPER（輸出者）一覧から選択する',
    chooseShipper: 'SHIPPER一覧から選択する',
    fillInWithNewShipperInfo: '手入力で変更する',
    shipper: 'SHIPPER（輸出者）',
    chooseShipperPrompt: 'SHIPPER（輸出者）を選択してください',
    ok: '次へ',
    alertClearingCargos: 'SHIPPERを編集して、集荷先と荷物の入力をやり直しますか？',
    createShipperDialogTitle: 'SHIPPER（輸出者）情報を入力してください',
    aboutShipper: () => (
      <>
        SHIPPER（ 輸出者）とは？
        <br />
        シッパー。荷送人または荷主のこと。一般的には、輸出者のこと。　
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    padding: '1rem 1rem',
  },
  shipperFilterContainer: {
    ...common.flexColumn,
    rowGap: '1rem',
    paddingTop: '1rem',
    paddingBottom: '1rem',
  },
  dialog: {
    maxWidth: '960px !important',
  },
  shipperListFilterContainer: {
    ...common.flexInline,
    columnGap: '1.5rem',
  },
  label: {
    minWidth: 'min-content',
    width: 'initial',
  },
  actionsContainer: {
    ...common.flexInline,
    columnGap: '1.5rem',
  },
  shipperItemContainer: {
    ...common.flexColumn,
    ...common.middleText,
    flex: 1,
    padding: '20px 20px',
    backgroundColor: theme.palette.grey[100],
  },
  shipperItemWrapper: {
    ...common.flexInline,
    columnGap: 1,
  },
  shipperItemIndicator: {
    backgroundColor: theme.palette.grey[100],
    width: 12,
  },
  shipperListContainer: {
    ...common.flexColumn,
    overflow: 'auto',
    height: 360,
    rowGap: 10,
  },
  shipperItemRow: {
    ...common.flexInline,
  },
  shipperItemColumn: {
    minWidth: 200,
  },
  shipperChooseOptionsContainer: {
    ...common.flexInline,
    gap: '20px',
  },
  infoTips: {
    ...common.textGray700,
    ...common.smallerText,
    ...common.flexInline,
    ...common.itemsStart,
    lineHeight: '1.125rem',
    minHeight: '4rem',
  },
}));

export const ShipperInfoInput: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { isDashboard, ownerId } = useBookingCreateContext();

  const [field, , helpers] = useField<number | undefined>('shipper');
  const [inputField, , inputHelpers] = useField<ContactFormType | undefined>('shipper_input');
  const [cargosPickupAddressesField, , cargosPickupAddressesHelpers] =
    useField<number[]>('cargos_pickup_addresses');
  const [cargosField, , cargosHelpers] = useField<Cargo2[]>('cargos2');

  const error = useErrorMessage('shipper');

  const [queryParams, setQueryParams] = useState<ContactSearchQuery>({
    company_name: '',
    principal_name: '',
  });

  const shippersSearchQueries = {
    ...queryParams,
    creator_id: isDashboard ? ownerId : undefined,
    contact_type: 'shipper',
    ordering: ['-use_count'],
  } satisfies ContactSearchQuery;

  const shippersQuery = isDashboard
    ? dashboardContactApis.useGet('/dashboard/contact', { queries: shippersSearchQueries })
    : contactApis.useGet('/contact', { queries: shippersSearchQueries });

  const shipperQueryParams = [
    {
      params: { id: field.value! },
    },
    {
      enabled: !!field.value,
    },
  ] as const;

  const shipperQuery = isDashboard
    ? dashboardContactApis.useGet('/dashboard/contact/:id', ...shipperQueryParams)
    : contactApis.useGet('/contact/:id', ...shipperQueryParams);

  const shippersData = shippersQuery.data;

  const initialValues = contactFormInitialValues;

  const { remove, refetch } = shippersQuery;

  const [shipperListOpen, setShipperListOpen] = useState(false);
  const [shipperCreateDialogOpen, setShipperCreateDialogOpen] = useState(false);

  const renderShipperChooseOptions = (change?: boolean) => {
    return (
      <div className={classes.shipperChooseOptionsContainer}>
        <Button
          data-testid="shipper-choose"
          color="primary"
          text={
            isDashboard
              ? strings.chooseShipper
              : change
              ? formStrings.change
              : strings.clientChooseShipper
          }
          size="small"
          shape="round"
          type="button"
          onClick={() => setShipperListOpen(true)}
        />
        {isDashboard && <div>or</div>}
        {isDashboard && (
          <Button
            data-testid="shipper-create"
            color="primary"
            text={strings.fillInWithNewShipperInfo}
            size="small"
            shape="round"
            type="button"
            onClick={() => setShipperCreateDialogOpen(true)}
          />
        )}
      </div>
    );
  };

  const renderShipperEmpty = () => {
    return renderShipperChooseOptions();
  };

  const renderShipperSelected = () => {
    return (
      <div>
        {inputField.value && <ContactDisplay contact={inputField.value} showSocialCreditId />}
        {shipperQuery.data && <ContactDisplay contact={shipperQuery.data} showSocialCreditId />}

        <div style={{ height: 14 }} />

        {renderShipperChooseOptions(true)}
      </div>
    );
  };

  const renderShipperChooseDialog = () => {
    return (
      <ActionableDialog
        open={shipperListOpen}
        onClose={() => setShipperListOpen(false)}
        classes={{
          paper: classes.dialog,
        }}
        muiDialogProps={{
          disablePortal: false,
        }}
      >
        <ActionableDialogTitle>{strings.chooseShipperPrompt}</ActionableDialogTitle>
        <ActionableDialogContent>
          <QueryContainer style={{ height: 478 }} queries={[shippersQuery]}>
            <ContactFilter
              contacts={shippersData ?? []}
              title={bookingInquiryStrings.shipper}
              selectedIds={field.value ? [field.value] : []}
              onChangeSelectedIds={(ids) => {
                const nextId = ids[ids.length - 1];
                if (cargosPickupAddressesField.value.length > 0 && nextId !== field.value) {
                  cargosPickupAddressesHelpers.setValue([]);
                  cargosHelpers.setValue(
                    cargosField.value.map((v) => ({
                      ...v,
                      pickup_address_index: 0,
                    })),
                  );
                }

                inputHelpers.setValue(undefined);
                helpers.setTouched(true);
                helpers.setValue(nextId);
              }}
              onAddContact={() =>
                window.open(url('/utilities/Contact/Shippers/AddShipper'), '_blank')
              }
              onEditContact={() =>
                window.open(url('/utilities/Contact/Shippers/Shippers'), '_blank')
              }
              onRefresh={() => {
                remove();
                refetch();
              }}
              query={queryParams}
              onChangeQuery={setQueryParams}
              actions={isDashboard ? 'none' : 'add-list'}
              isEnglishNameRequired
              isZipcodeRequired
              editUrl={(contact) =>
                (isDashboard
                  ? contact.user && `${url('/admin/members/MemberDetails')}?id=${contact.user}`
                  : contact.id &&
                    `${url('/utilities/Contact/Shippers/ShipperDetails')}?id=${contact.id}`) || ''
              }
            />
          </QueryContainer>
        </ActionableDialogContent>

        <ActionableDialogContentDivider />
        <div style={{ padding: '1rem 24px' }}>
          <Button
            color="primary"
            text={strings.ok}
            size="small"
            data-testid="close-contact-filter"
            onClick={() => {
              setShipperListOpen(false);
            }}
            style={{ width: '100%' }}
          />
        </div>
      </ActionableDialog>
    );
  };

  const renderShipperCreateDialog = () => {
    return (
      <Formik
        initialValues={initialValues}
        validate={(values) => ({
          ...(shippersData?.find(
            (v) => v.social_credit_id && v.social_credit_id === values.social_credit_id,
          )
            ? { social_credit_id: contactStrings.usciDuplicatedError }
            : {}),
          ...createValidate(contactForm)(values),
        })}
        onSubmit={async (values) => {
          helpers.setValue(0, false);
          helpers.setTouched(true, false);
          inputHelpers.setTouched(true, false);
          inputHelpers.setValue(values, true);
          // Should validate what makes it valid...
          // The internal of formik needs investigation.
          setShipperCreateDialogOpen(false);
        }}
      >
        {({ handleSubmit }) => (
          <Form>
            <SubmittingOverlay />
            <ActionableDialog
              open={shipperCreateDialogOpen}
              onClose={() => setShipperCreateDialogOpen(false)}
              classes={{
                paper: classes.dialog,
              }}
              muiDialogProps={{
                disablePortal: false,
              }}
            >
              <ActionableDialogTitle textAlign="center">
                {strings.createShipperDialogTitle}
              </ActionableDialogTitle>
              <ActionableDialogContent>
                <ContactForm zipcodeRequired contactType="shipper" />
              </ActionableDialogContent>

              <ActionableDialogContentDivider />
              <div style={{ padding: '1rem 24px' }}>
                <Button
                  color="primary"
                  text={formStrings.ok}
                  size="small"
                  shape="round"
                  data-testid="close-contact-create"
                  onClick={() => {
                    handleSubmit();
                  }}
                  style={{ margin: '0 auto', padding: '2px 36px' }}
                />
              </div>
            </ActionableDialog>
          </Form>
        )}
      </Formik>
    );
  };

  return (
    <div className={classes.container}>
      {field.value === undefined && renderShipperEmpty()}
      {field.value !== undefined && renderShipperSelected()}
      {error != null && (
        <>
          <VerticalSpace height={10} />
          <div className={clsx(classes.textError, classes.smallerText)}>{error ?? 'Error'}</div>
        </>
      )}
      {field.value === undefined && (
        <>
          <VerticalSpace height={10} />
          <div className={classes.infoTips}>
            <img src={infoIcon} style={{ height: 13.75, width: 13.75 }} alt="info" />
            <div style={{ width: 10 }} />
            {strings.aboutShipper()}
          </div>
        </>
      )}
      {renderShipperChooseDialog()}
      {renderShipperCreateDialog()}
    </div>
  );
};
